// PLAN DE RESPUESTA (Central Día 1 §1/§13) — superficie interna de supervisor.
// Configura el plan por persona: responsable/autorizante, decisión de emergencia, acción final y
// acción al vencer espera por familia, autorización FC/FR sin verificación, y flags de contactos
// (orden, recibe avisos, puede confirmar estado, nivel de autoridad). Muestra commissioning + faltantes.
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, ShieldAlert, Clock, Save } from "lucide-react";
import { DateField } from "@/components/forms/fields";

const FAM_LABEL = {
  caida: "Caída", boton_panico: "Botón de pánico", salida_bano_sin_retorno: "Salida de baño sin retorno",
  permanencia_prolongada_zona: "Permanencia prolongada en zona", fuera_de_cama: "Fuera de cama (HM)",
  fuera_de_cama_nocturno_sin_retorno: "Fuera de cama nocturno (EKH)", fuera_de_zona_permitida: "Fuera de zona permitida",
  excepcion_frecuencia_cardiaca: "Frecuencia cardíaca", excepcion_frecuencia_respiratoria: "Frecuencia respiratoria",
};
const FC_FR = ["excepcion_frecuencia_cardiaca", "excepcion_frecuencia_respiratoria"];
const NIVELES = ["informativo", "autorizado", "responsable_autorizado_plan"];

function Estado({ status }) {
  const map = {
    LISTO: { c: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", i: ShieldCheck },
    PENDIENTE: { c: "bg-amber-500/15 text-amber-700 dark:text-amber-300", i: Clock },
    BLOQUEADO: { c: "bg-[#FE6A55]/15 text-[#B93B29] dark:text-[#FE6A55]", i: ShieldAlert },
  }[status] || { c: "bg-slate-200 text-slate-600", i: Clock };
  const I = map.i;
  return <span data-testid="plan-commissioning-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold ${map.c}`}><I className="h-3.5 w-3.5" /> {status}</span>;
}

export default function PlanRespuestaDialog({ open, onOpenChange, personaId }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [contactos, setContactos] = useState([]);

  const cargar = useCallback(async () => {
    if (!personaId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/personas/${personaId}/plan-respuesta`);
      setData(d);
      const p = d.plan || {};
      setForm({
        responsable_plan: p.responsable_plan || "", autorizado_por: p.autorizado_por || "",
        autorizante_relacion: p.autorizante_relacion || "", fecha_autorizacion: p.fecha_autorizacion || "",
        decision_emergencia: p.decision_emergencia || "", instrucciones_especiales: p.instrucciones_especiales || "",
        seguimiento_post_emergencia: !!p.seguimiento_post_emergencia,
        plantillas_por_tipo: p.plantillas_por_tipo || {},
        autoriza_emergencia_sin_verificacion: p.autoriza_emergencia_sin_verificacion || {},
        familias_activas: p.familias_activas || (d.catalogos?.familias || []),
      });
      setContactos(d.contactos || []);
    } catch { toast.error("No se pudo cargar el plan."); }
    finally { setLoading(false); }
  }, [personaId]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const setPPT = (fam, key, val) => setForm((f) => ({
    ...f, plantillas_por_tipo: { ...f.plantillas_por_tipo, [fam]: { ...(f.plantillas_por_tipo[fam] || {}), [key]: val } },
  }));
  const setAut = (fam, key, val) => setForm((f) => ({
    ...f, autoriza_emergencia_sin_verificacion: {
      ...f.autoriza_emergencia_sin_verificacion,
      [fam]: { ...(f.autoriza_emergencia_sin_verificacion[fam] || {}), [key]: val },
    },
  }));
  const setC = (i, key, val) => setContactos((cs) => cs.map((c, j) => j === i ? { ...c, [key]: val } : c));

  const guardar = async () => {
    setBusy(true);
    try {
      // 1) flags de contactos -> persona
      const rels = contactos.map((c) => ({
        contacto_id: c.contacto_id, orden: c.orden, relacion_familiar: c.relacion,
        recibe_avisos: !!c.recibe_avisos, puede_confirmar_estado: !!c.puede_confirmar_estado,
        nivel_autoridad: c.nivel_autoridad,
      }));
      if (rels.length) await api.put(`/personas/${personaId}`, { contactos: rels });
      // 2) plan
      const { data: saved } = await api.put(`/personas/${personaId}/plan-respuesta`, form);
      toast.success(`Plan guardado — commissioning: ${saved.commissioning_status}`);
      await cargar();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const familias = data?.catalogos?.familias || form.familias_activas || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plan-respuesta-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span>Plan de respuesta · {data?.persona_nombre || ""}</span>
            {data && <Estado status={data.commissioning_status} />}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-5 text-sm">
            {data.commissioning_faltantes?.length > 0 && (
              <div data-testid="plan-faltantes" className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-900/10">
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Faltantes para quedar LISTO</p>
                <ul className="list-inside list-disc text-xs text-amber-800 dark:text-amber-200">
                  {data.commissioning_faltantes.map((f) => <li key={f}>{f.replace(/::/g, " · ").replace(/_/g, " ")}</li>)}
                </ul>
              </div>
            )}

            {/* Responsable / autorizante */}
            <section className="grid grid-cols-2 gap-3">
              <div><Label>Responsable del plan</Label><Input data-testid="plan-responsable" value={form.responsable_plan} onChange={(e) => setForm({ ...form, responsable_plan: e.target.value })} /></div>
              <div><Label>Autorizado por</Label><Input data-testid="plan-autorizante" value={form.autorizado_por} onChange={(e) => setForm({ ...form, autorizado_por: e.target.value })} /></div>
              <div><Label>Relación del autorizante</Label><Input value={form.autorizante_relacion} onChange={(e) => setForm({ ...form, autorizante_relacion: e.target.value })} /></div>
              <div><Label>Fecha de autorización</Label><DateField testId="plan-fecha-autorizacion" value={form.fecha_autorizacion} onChange={(v) => setForm({ ...form, fecha_autorizacion: v })} /></div>
              <div className="col-span-2"><Label>Decisión crítica de emergencia</Label><Input data-testid="plan-decision-emergencia" placeholder="p.ej. solicitar servicios de emergencia directo" value={form.decision_emergencia} onChange={(e) => setForm({ ...form, decision_emergencia: e.target.value })} /></div>
            </section>

            <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-[#1E2D4A]">
              <Switch data-testid="plan-seguimiento" checked={form.seguimiento_post_emergencia} onCheckedChange={(v) => setForm({ ...form, seguimiento_post_emergencia: v })} />
              <span>Requiere seguimiento post-emergencia (bloquea el cierre hasta cumplirlo)</span>
            </label>

            {/* Contactos: orden + flags */}
            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Contactos</p>
              {contactos.length === 0 && <p className="text-xs text-slate-400">Sin contactos configurados en la ficha.</p>}
              <div className="space-y-2">
                {contactos.map((c, i) => (
                  <div key={c.contacto_id} data-testid={`plan-contacto-${c.contacto_id}`} className="rounded-lg border border-slate-200 p-2.5 dark:border-[#1E2D4A]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-100">{c.nombre || "—"} <span className="font-normal text-slate-400">· {c.telefono_display || "sin teléfono"}</span></p>
                      <div className="flex items-center gap-1"><Label className="text-[10px]">Orden</Label>
                        <Input type="number" className="h-7 w-14" value={c.orden ?? ""} onChange={(e) => setC(i, "orden", e.target.value === "" ? null : Number(e.target.value))} /></div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                      <label className="flex items-center gap-1.5"><Switch data-testid={`plan-c-avisos-${c.contacto_id}`} checked={c.recibe_avisos} onCheckedChange={(v) => setC(i, "recibe_avisos", v)} /> Recibe avisos</label>
                      <label className="flex items-center gap-1.5"><Switch data-testid={`plan-c-confirma-${c.contacto_id}`} checked={c.puede_confirmar_estado} onCheckedChange={(v) => setC(i, "puede_confirmar_estado", v)} /> Puede confirmar estado</label>
                      <select data-testid={`plan-c-nivel-${c.contacto_id}`} value={c.nivel_autoridad} onChange={(e) => setC(i, "nivel_autoridad", e.target.value)} className="rounded border border-slate-200 bg-white px-2 py-1 dark:border-[#1E2D4A] dark:bg-[#0B111E]">
                        {NIVELES.map((n) => <option key={n} value={n}>{n.replace(/_/g, " ")}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Por familia: acción final + acción al vencer espera */}
            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Terminal por familia</p>
              <div className="space-y-2">
                {familias.map((fam) => (
                  <div key={fam} data-testid={`plan-fam-${fam}`} className="rounded-lg border border-slate-200 p-2.5 dark:border-[#1E2D4A]">
                    <p className="mb-1.5 text-xs font-bold text-[#012472] dark:text-[#80B3FF]">{FAM_LABEL[fam] || fam}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Acción final sin verificación" value={form.plantillas_por_tipo[fam]?.accion_final_sin_verificacion || ""} onChange={(e) => setPPT(fam, "accion_final_sin_verificacion", e.target.value)} />
                      <Input placeholder="Acción si vence la espera" value={form.plantillas_por_tipo[fam]?.accion_si_compromiso_vence || ""} onChange={(e) => setPPT(fam, "accion_si_compromiso_vence", e.target.value)} />
                    </div>
                    {FC_FR.includes(fam) && (
                      <div className="mt-2 rounded border border-[#FE6A55]/30 bg-[#FE6A55]/5 p-2">
                        <label className="flex items-center gap-2 text-xs"><Switch data-testid={`plan-fcfr-${fam}`} checked={!!form.autoriza_emergencia_sin_verificacion[fam]?.autorizado} onCheckedChange={(v) => setAut(fam, "autorizado", v)} /> Autoriza emergencia SIN verificación</label>
                        {form.autoriza_emergencia_sin_verificacion[fam]?.autorizado && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <Input placeholder="Autorizante" value={form.autoriza_emergencia_sin_verificacion[fam]?.autorizante || ""} onChange={(e) => setAut(fam, "autorizante", e.target.value)} />
                            <Input placeholder="Fuente del umbral" value={form.autoriza_emergencia_sin_verificacion[fam]?.fuente_umbral || ""} onChange={(e) => setAut(fam, "fuente_umbral", e.target.value)} />
                            <Input placeholder="Versión" value={form.autoriza_emergencia_sin_verificacion[fam]?.version || ""} onChange={(e) => setAut(fam, "version", e.target.value)} />
                            <DateField testId={`plan-fcfr-fecha-${fam}`} value={form.autoriza_emergencia_sin_verificacion[fam]?.fecha || ""} onChange={(v) => setAut(fam, "fecha", v)} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 dark:border-[#1E2D4A]">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              <Button data-testid="plan-guardar" disabled={busy} onClick={guardar} className="bg-[#012472] text-white hover:bg-[#012472]/90">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar plan
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
